import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Show, SignIn, SignUp } from "@clerk/react";
import { Toaster } from "@/components/ui/sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import StaffApp from "@/pages/StaffApp";
import KioskPage from "@/pages/KioskPage";
import RemotePage from "@/pages/RemotePage";
import { PenLine, Languages, CalendarDays, FileClock } from "lucide-react";
import { MaintenanceGate } from "@/components/MaintenanceGate";


const FEATURES = [[PenLine, "iPad & remote e-signing"], [Languages, "English, Español, Kreyòl"],
  [CalendarDays, "Provider schedules"], [FileClock, "Time-recorded PDFs"]];
const PRACTICES = ["MindCare", "Hope Valley", "Apex Dental"];

function AuthPage({ children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 xl:p-16 ace-hero relative overflow-hidden" data-testid="landing-hero">
        <img src="/ace-logo.png" alt="ACE eSIGN - Secure. Compliant. Efficient." className="w-72 xl:w-80 fade-in" data-testid="brand-logo" />
        <div className="relative z-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-[var(--ace-navy)]">Consent, signed<br />and filed. <span className="text-[var(--ace-sky)]">Correctly.</span></h1>
          <p className="mt-6 text-slate-600 max-w-md text-base">Multi-organization patient consent and e-signature workspace. Prototype with synthetic patients only - never enter real PHI.</p>
          <div className="grid grid-cols-2 gap-3 mt-10 max-w-lg">{FEATURES.map(([I, l], i) => (
            <div key={l} className="ace-feature flex items-center gap-3 rounded-xl px-3 py-3 bg-white border border-sky-100 shadow-sm" style={{ animationDelay: `${120 + i * 80}ms` }}><span className="w-8 h-8 rounded-lg grid place-items-center bg-[var(--ace-sky-soft)] text-[var(--ace-sky)]"><I className="w-4 h-4" /></span><span className="text-sm font-medium text-[var(--ace-navy)]">{l}</span></div>))}</div>
          <div className="flex flex-wrap gap-2 mt-6">{PRACTICES.map((n) => (
            <span key={n} className="text-xs rounded-full px-3 py-1 bg-white border border-emerald-200 text-[var(--ace-navy)] flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[var(--ace-green)]" />{n}</span>))}</div>
        </div>
        <p className="text-xs text-slate-500 relative z-10">Staff accounts are individual. Access is granted per organization by an administrator.</p>
      </div>
      <div className="flex flex-col items-center justify-center p-6 ace-auth-side min-w-0">
        <img src="/ace-logo.png" alt="ACE eSIGN" className="lg:hidden w-52 mb-8" data-testid="brand-logo-mobile" />
        {children}
        <p className="hidden lg:block mt-8 text-sm tracking-wide text-sky-100/80 italic">Secure. Compliant. Efficient.</p>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <MaintenanceGate>
      <ClerkAuthProvider>
        <Routes>
          <Route path="/sign-in/*" element={<AuthPage><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthPage>} />
          <Route path="/sign-up/*" element={<AuthPage><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthPage>} />
          <Route path="/kiosk/:token" element={<KioskPage />} />
          <Route path="/r/:token" element={<RemotePage />} />
          <Route path="/*" element={<>
            <Show when="signed-in"><StaffApp /></Show>
            <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
          </>} />
        </Routes>
        <Toaster richColors position="top-right" />
      </ClerkAuthProvider>
      </MaintenanceGate>
    </BrowserRouter>
  );
}
